import { useCallback, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import {
  Box,
  Container,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import CssBaseline from "@material-ui/core/CssBaseline";
import { Add } from "@material-ui/icons";
import SearchRoundedIcon from "@material-ui/icons/SearchRounded";
import {
  EditPromotionCodeModal,
  HeaderWithButtons,
  Searchbar,
  SortButtonAndMenu,
} from "../components-molecules";
import { PromotionCodeList } from "../components-organisms/PromotionCodeList";
import { AddPromotionCodeModal } from "../components-molecules/AddPromotionCodeModal";
import {
  clearGeneratedPromoCode,
  clearPromotionCodes,
  getPromotionCodes,
  getReferralVoucherNames,
  IPromoCodeNameFilterItem,
  IPromotionCodeFilter,
} from "../redux/actions/promotionCodes";
import {
  filterOptions as defaultFilters,
  ISortItem,
  sortTypes,
} from "../utils/promo-code/headerOptions";
import { OrderSortType } from "../utils/promo-code/groupHeaders";
import { FilterButtonWithTitle } from "../components-molecules/FilterButtonWithTitle";
import { ButtonPrimary } from "../components-atoms";
import { buttonHoverAndActive, textButtonHoverAndActive } from "../utils/styles/buttonStyles";
const timeoutDuration = 1500;

export const PromoAndVoucherCodes = (props: any) => {
  const classes = useStyles();
  const [page, setPage] = useState<number>(1);
  const [modal, setModal] = useState<string>("");
  const [sortType, setSortType] = useState<OrderSortType>("Expires");
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const [filterOptions, setFilterOptions] = useState(defaultFilters);
  const [filter, setFilter] = useState<IPromotionCodeFilter>({
    DiscountType: [],
    UseType: [],
    CodeType: [],
    CustomerIds: []
  });
  const [query, setQuery] = useState<string>("");
  const dispatch = useDispatch();
  const [codes]: [any] = useSelector((state: any) => [
    state.PromotionCodesSymbolsReducer.data as any,
  ]);

  const getFilters = () => {
    let newFilters = [...defaultFilters];
    getReferralVoucherNames((res: IPromoCodeNameFilterItem[]) => {
      newFilters.push({
        key: "CustomerIds",
        title: "User",
        options: res.map(r => ({ value: r.Id, title: `${r.FirstName} ${r.LastName}`, helperText: `${r.PhoneNumber} - ${r.Email}` })),
      })
    }, (err) => {
      console.log(err)
    });
    setFilterOptions(newFilters);
  }

  const getPromoCodes = useCallback(() => {
    getFilters();
    dispatch(
      getPromotionCodes({
        Size: 15,
        Page: page,
        Query: query,
        OrderBy: sortType,
        Filter: filter,
      })
    );
  }, [dispatch, query, sortType, filter, page]);

  useEffect(() => {
    getPromoCodes();
  }, [query, getPromoCodes]);

  const loadMore = () => {
    setPage(page + 1);
  };

  const onFilterSubmit = (thisFilter: any): void => {
    let tempFilter: IPromotionCodeFilter = {
      DiscountType: thisFilter["DiscountType"] as string[],
      UseType: thisFilter["UseType"] as string[],
      CodeType: thisFilter["CodeType"] as string[],
      CustomerIds: thisFilter["CustomerIds"] as string[],
    };
    if (thisFilter["MinimumSpend"]?.length === 1) {
      tempFilter["MinimumSpend"] = thisFilter["MinimumSpend"][0] as boolean;
    }
    if (thisFilter["Status"]?.length === 1) {
      tempFilter["Status"] = thisFilter["Status"][0] as boolean;
    }

    dispatch(clearPromotionCodes());
    setPage(1);
    setFilter(tempFilter as IPromotionCodeFilter);
  };

  const getLoadedCount = () => {
    return codes?.Data?.reduce((p: number, c) => p + c.PromoCodes?.length, 0);
  };

  return (
    <Box className={classes.container}>
      <CssBaseline />
      <Container className={classes.root}>
        <CssBaseline />
        <HeaderWithButtons fullWidthTitle>
          <HeaderWithButtons.ButtonArea>
            <div className={classes.buttonSet}>
              <SortButtonAndMenu
                data-automation="sort-button"
                menuItems={sortTypes.map((sortItem: ISortItem) => ({
                  title: sortItem.title,
                  value: sortItem.value,
                  handleSelect: () => {
                    if (sortItem.value === sortType) return;
                    dispatch(clearPromotionCodes());
                    setPage(1);
                    setSortType(sortItem.value);
                  },
                }))}
                selectedSortType={sortType}
              />
              <FilterButtonWithTitle
                defaultOption="DiscountType"
                data-automation="filter-button"
                searchableList={["CustomerIds"]}
                onSubmit={onFilterSubmit}
                inButtonSet
                options={filterOptions}
                filter={filter}
              />
              <IconButton
                data-automation="search-button"
                className={classes.searchIconButton}
                color="primary"
                onClick={() => setSearchOpen(!searchOpen)}
              >
                <SearchRoundedIcon
                  color="primary"
                  className={classes.searchIcon}
                />
              </IconButton>
              <ButtonPrimary
                data-automation="add-code-button"
                variant="contained"
                color="primary"
                disableElevation
                className={classes.primaryButton}
                style={{
                  paddingLeft: props.loading ? "12px" : "16px",
                }}
                startIcon={<Add />}
                onClick={() => setModal("add")}
              >
                Add Code
              </ButtonPrimary>
            </div>
          </HeaderWithButtons.ButtonArea>
        </HeaderWithButtons>
        <Searchbar
          query={query}
          isOpen={searchOpen}
          placeholder="Search Promotion Codes"
          onCancel={() => {
            if (query.length > 0) {
              dispatch(clearPromotionCodes());
            }
            setSearchOpen(false);
            setQuery("");
          }}
          onChange={(event) => {
            dispatch(clearPromotionCodes());
            debounce((e) => setQuery(e.target.value), timeoutDuration)(event);
          }}
        />
        <PromotionCodeList
          listKey="PromoCodes"
          query={query}
          filter={filter}
          sortType={sortType}
          onEdit={() => setModal("edit")}
          loadedCount={getLoadedCount()}
          loadMore={loadMore}
        />
      </Container>
      <AddPromotionCodeModal
        visible={modal === "add"}
        onClose={() => {
          setModal("");
          dispatch(clearGeneratedPromoCode());
        }}
      />
      <EditPromotionCodeModal
        visible={modal === "edit"}
        onClose={() => setModal("")}
        onSubmit={() => {
          dispatch(clearPromotionCodes());
          getPromoCodes();
        }}
        onDeactivate={() => {
          dispatch(clearPromotionCodes());
          getPromoCodes();
        }}
      />
    </Box>
  );
};

const useStyles = makeStyles({
  container: {
    display: "flex",
    justifyContent: "center",
  },
  root: {
    maxWidth: "840px",
    paddingLeft: 0,
    paddingRight: 0,
  },
  buttonSet: {
    display: "flex",
    flexDirection: "row",
    paddingRight: "24px",
  },
  primaryButton: {
    marginLeft: 8,
    borderRadius: 8,
    ...buttonHoverAndActive
  },
  searchIcon: {
    width: 24,
    height: 24,
  },
  searchIconButton: {
    width: 40,
    height: 40,
    marginLeft: 8,
    ...textButtonHoverAndActive
  },
});
